/**
 * FiscFormLinkModal.jsx · v3.9.4 bis · Componente compartido
 * ═══════════════════════════════════════════════════════════════════
 * Modal reutilizable para vincular un tipo de solución LEGACY (name/code)
 * con uno de los 38 formularios del constructor de fiscalización.
 * Persiste el mapeo en `fisc_matrix_aliases` (`plan_name` + `plan_code`).
 *
 * Uso:
 *   <FiscFormLinkModal
 *      st={{ name, code }}         // tipo de solución legacy
 *      onClose={() => setModal(null)}
 *   />
 */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, AlertCircle, Link2, ExternalLink, Save, X, ClipboardList,
} from "lucide-react";

export default function FiscFormLinkModal({ st, onClose }) {
  const [linked, setLinked] = useState(null);
  const [matrixTypes, setMatrixTypes] = useState([]);
  const [selectedMatrixId, setSelectedMatrixId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!st) return;
    setLoading(true);
    Promise.all([
      api.get("/v1/fisc-matrix/link-by-legacy", { params: { name: st.name, code: st.code || undefined } }),
      api.get("/v1/fisc-matrix/solution-types"),
    ])
      .then(([r1, r2]) => {
        setLinked(r1.data);
        setMatrixTypes(r2.data.items || []);
        if (r1.data.matched) setSelectedMatrixId(String(r1.data.solution_type.id));
      })
      .catch(() => toast.error("No se pudo cargar el vínculo"))
      .finally(() => setLoading(false));
  }, [st]);

  const save = async () => {
    if (!selectedMatrixId) return toast.error("Elegí un formulario del constructor");
    setSaving(true);
    try {
      await api.post("/v1/fisc-matrix/link-by-legacy", {
        name: st.name,
        code: st.code || null,
        target_matrix_id: selectedMatrixId,
      });
      toast.success("Formulario vinculado. La app móvil ya usará este mapeo.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setSaving(false); }
  };

  const currentForm = linked?.form_definition;
  const currentMatrix = linked?.solution_type;
  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    const list = query
      ? matrixTypes.filter(m =>
          (m.tipo_hija || "").toLowerCase().includes(query) ||
          (m.categoria || "").toLowerCase().includes(query) ||
          (m.tipo_padre || "").toLowerCase().includes(query)
        )
      : matrixTypes;
    const g = {};
    list.forEach(m => {
      const k = `${m.categoria} · ${m.tipo_padre}`;
      if (!g[k]) g[k] = [];
      g[k].push(m);
    });
    return g;
  }, [matrixTypes, q]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="fisc-form-link-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <header className="px-5 py-3 border-b flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
            <ClipboardList size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-[#0A3747]">Formulario de fiscalización</div>
            <div className="text-[11px] text-gray-500">{st.name} · <code className="bg-gray-100 px-1 rounded">{st.code || "sin código"}</code></div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="fisc-form-link-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              📌 Formulario actual
            </div>
            {loading && <div className="text-[11px] text-gray-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Cargando…</div>}
            {!loading && !currentMatrix && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px]">
                <div className="font-bold text-amber-800 mb-1 flex items-center gap-1"><AlertCircle size={12} /> Sin vínculo</div>
                <div className="text-amber-700">Este tipo de solución legacy aún no está mapeado a la matriz de fiscalización. Elegí uno de la derecha →</div>
              </div>
            )}
            {!loading && currentMatrix && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
                <div className="text-[11px] font-bold text-emerald-800 flex items-center gap-1">
                  <Link2 size={11} /> Vinculado (match: <span className="font-mono">{currentMatrix.matched_by}</span>)
                </div>
                <div className="text-[12px] font-bold text-[#0A3747]">{currentMatrix.tipo_hija}</div>
                <div className="text-[10px] text-gray-500">{currentMatrix.categoria} · {currentMatrix.tipo_padre}</div>
                {currentForm && (
                  <>
                    <div className="pt-2 border-t border-emerald-200 text-[10px] uppercase tracking-widest font-bold text-emerald-700">
                      {currentForm.sections?.length || 0} secciones · {(currentForm.sections || []).reduce((a, s) => a + (s.fields?.length || 0), 0)} campos
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1 mt-1">
                      {(currentForm.sections || []).map((s, i) => (
                        <div key={i} className="bg-white rounded border border-gray-100 p-1.5">
                          <div className="text-[10px] font-bold text-[#0A3747]">{s.title}</div>
                          <div className="text-[9px] text-gray-500">{(s.fields || []).length} campos</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <a href="/fiscalizacion" target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-800">
                  Abrir constructor <ExternalLink size={10} />
                </a>
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              🔀 Reasignar a…
            </div>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar…"
              className="w-full mb-2 px-2 py-1 rounded border border-gray-300 text-[11px]"
              data-testid="fisc-form-link-search"
            />
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded" data-testid="fisc-form-link-list">
              {Object.entries(grouped).map(([g, list]) => (
                <div key={g}>
                  <div className="px-2 py-1 bg-slate-100 text-[9px] font-bold uppercase tracking-widest text-slate-500 sticky top-0">{g}</div>
                  {list.map(m => (
                    <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 text-[11px] cursor-pointer border-b border-gray-100 ${String(m.id) === selectedMatrixId ? "bg-yellow-100 font-bold" : "hover:bg-slate-50"}`}>
                      <input
                        type="radio"
                        name="matrix-target"
                        value={m.id}
                        checked={String(m.id) === selectedMatrixId}
                        onChange={() => setSelectedMatrixId(String(m.id))}
                        data-testid={`fisc-form-link-radio-${m.id}`}
                      />
                      <span className="flex-1">{m.tipo_hija}</span>
                    </label>
                  ))}
                </div>
              ))}
              {Object.keys(grouped).length === 0 && !loading && (
                <div className="text-center py-4 text-[10px] text-gray-400 italic">Sin resultados</div>
              )}
            </div>
          </div>
        </div>

        <footer className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between gap-3">
          <div className="text-[10px] text-slate-500">
            🔒 Se persiste en <code>fisc_matrix_aliases</code> y lo usa la app móvil al abrir la OT.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 rounded border border-gray-300 text-xs font-bold">Cancelar</button>
            <button
              onClick={save}
              disabled={saving || !selectedMatrixId}
              className="px-4 py-1.5 rounded bg-blue-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
              data-testid="fisc-form-link-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Vincular
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
